import { useState } from 'react'
import type { ReactNode } from 'react'
import { CommandCenterShell, CC_INK, CC_MONO, CC_FONT } from '../shell'
import { MessageIcon, MeetingIcon, UsersIcon, TaskIcon } from '../shell/icons'
import { StatusBadge } from '../operations'
import CommsQuantumBackdrop from './CommsQuantumBackdrop'
import { COMMS_ROOT, TEAM_ROOT, COMMS_ACCENT, type Channel } from './commsData'
import './comms-surface.css'

/**
 * CommsShell — shared frame + channel rail for the V2 Communications/Team lane.
 *
 * PHASE_2I: renders inside the shared CommandCenterShell with a Communications|Team
 * top bar, and a 3-pane body (left rail · center · right rail). REALTIME is
 * deferred (a REALTIME_DEFERRED badge sits on comms surfaces); nothing here
 * fabricates channels, members, messages, or tasks.
 *
 * Chat #2 conversion: presentation moved out of inline style objects into
 * comms-surface.css (which consumes the shared .sqv-surface foundation). The
 * surface root is `.sqv-surface .sqv-comms`; the comms accent + INK ramp live
 * as CSS custom properties. The mobile pane-switch and the sqv2-msg-actions
 * hover/focus reveal stay in the inline <style> seam below (pinned to the SSR
 * output by tests). COMMS_ACCENT is re-exported from commsData (no hex literal
 * lives in this presentation file).
 */

export { COMMS_ACCENT }
export const INK = CC_INK
export const MONO = CC_MONO
export const FONT = CC_FONT

/**
 * Shared inline-style objects kept for the (un-converted) ConferenceCalls
 * surface, which still consumes them. The converted surfaces use the mirrored
 * `.sqv-comms-card` / `.sqv-comms-title` / `.sqv-comms-input` / `.sqv-comms-btn`
 * classes instead. Neither carries a hex literal (accent/INK are references).
 */
export const card: React.CSSProperties = { padding: '14px 16px', borderRadius: 12, background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }
export const sectionTitle: React.CSSProperties = { margin: '0 0 10px', fontSize: 10, letterSpacing: '0.16em', fontWeight: 800, fontFamily: MONO, color: COMMS_ACCENT, textTransform: 'uppercase' }
export const inputStyle: React.CSSProperties = { padding: '8px 11px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.08)', background: 'rgba(255,255,255,0.03)', color: INK.primary, fontSize: 13, fontFamily: FONT, outline: 'none', boxSizing: 'border-box', width: '100%' }
export const btn: React.CSSProperties = { padding: '8px 13px', borderRadius: 8, cursor: 'pointer', background: COMMS_ACCENT + '18', border: '1px solid ' + COMMS_ACCENT + '33', color: COMMS_ACCENT, fontSize: 12, fontWeight: 800, fontFamily: MONO }

export interface CommsFrameProps {
  activeTop: 'communications' | 'team'
  breadcrumb: string[]
  title: string
  statusBadge?: ReactNode
  realtimeBadge?: boolean
  leftRail?: ReactNode
  center: ReactNode
  rightRail?: ReactNode
  onHome?: () => void
  onBackCommandCenter?: () => void
  onNavTop?: (to: string) => void
  /**
   * V2 surface id (e.g. "team-home", "channel"). Stamped as the shared
   * `sqv-surface` class + `data-sqv-surface` attribute on the surface root so
   * the shared V2 stylesheet (and its inline-style guard) can target this
   * surface.
   */
  surface?: string
}

/** Honest empty/loading/error/auth state block, reused across surfaces. */
export function StateBlock({ loading, error, authRequired, empty, emptyLabel }: { loading?: boolean; error?: string | null; authRequired?: boolean; empty?: boolean; emptyLabel: string }) {
  if (authRequired) return <p className="sqv-comms-state">Sign in to the live cockpit to load this — nothing is shown until then (no fabricated data).</p>
  if (loading) return <p className="sqv-comms-state">Loading…</p>
  if (error) return <p className="sqv-comms-state sqv-comms-state-error">{error}</p>
  if (empty) return <p className="sqv-comms-state">{emptyLabel}</p>
  return null
}

/**
 * MessageCenterNav — the Message Center's left-rail navigation card. Lists the
 * first-class Message Center destinations (home, Conference Calls, Team, Tasks)
 * so every comms surface shares one map. Conference Calls is the VIDEO layer
 * (Daily.co) — distinct from the internal Daily Meetings record.
 */
export type MessageCenterNavKey = 'home' | 'channels' | 'dms' | 'conference_calls' | 'team' | 'tasks'
export function MessageCenterNav({
  active, onOpenHome, onOpenConferenceCalls, onOpenTeam, onOpenTasks,
}: {
  active?: MessageCenterNavKey
  onOpenHome?: () => void
  onOpenConferenceCalls?: () => void
  onOpenTeam?: () => void
  onOpenTasks?: () => void
}) {
  const row = (key: MessageCenterNavKey, label: string, icon: ReactNode, onClick?: () => void) => {
    const on = active === key
    return (
      <button key={key} type="button" onClick={onClick} className="sqv-comms-navrow sqv-comms-navrow-flex" data-on={on ? 'true' : 'false'}>
        <span className="sqv-comms-navrow-icon">{icon}</span> {label}
      </button>
    )
  }
  return (
    <div className="sqv-comms-card">
      <h3 className="sqv-comms-title">Message Center</h3>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {row('home', 'Message Center', <MessageIcon size={13} color={INK.faint} still />, onOpenHome)}
        {row('conference_calls', 'Conference Calls', <MeetingIcon size={13} color={INK.faint} still />, onOpenConferenceCalls)}
        {row('team', 'Team', <UsersIcon size={13} color={INK.faint} />, onOpenTeam)}
        {row('tasks', 'Tasks', <TaskIcon size={13} color={INK.faint} />, onOpenTasks)}
      </div>
    </div>
  )
}

export function ChannelRail({
  channels, dms, activeId, loading, error, authRequired,
  onOpenChannel, onOpenDm, onCreateChannel, onOpenTeam,
  onOpenHome, onOpenConferenceCalls, onOpenTasks, activeNav,
}: {
  channels: Channel[]; dms: Channel[]; activeId?: string
  loading?: boolean; error?: string | null; authRequired?: boolean
  onOpenChannel?: (id: string) => void; onOpenDm?: (id: string) => void
  onCreateChannel?: () => void; onOpenTeam?: () => void
  onOpenHome?: () => void; onOpenConferenceCalls?: () => void; onOpenTasks?: () => void
  activeNav?: MessageCenterNavKey
}) {
  const railBtn = (label: string, on: boolean, onClick?: () => void, prefix = '#') => (
    <button key={label} type="button" onClick={onClick} className="sqv-comms-navrow" data-on={on ? 'true' : 'false'}>
      <span className="sqv-comms-railrow-prefix">{prefix}</span> {label}
    </button>
  )
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <MessageCenterNav active={activeNav} onOpenHome={onOpenHome} onOpenConferenceCalls={onOpenConferenceCalls} onOpenTeam={onOpenTeam} onOpenTasks={onOpenTasks} />
      <div className="sqv-comms-card">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
          <h3 className="sqv-comms-title sqv-comms-title-flush">Channels</h3>
          <button type="button" onClick={onCreateChannel} title="Create a channel" className="sqv-comms-btn sqv-comms-btn-sm">+</button>
        </div>
        {authRequired || loading || error || channels.length === 0 ? (
          <StateBlock loading={loading} error={error} authRequired={authRequired} empty={channels.length === 0} emptyLabel="No channels yet — create one to start." />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {channels.map((c) => railBtn(c.name, c.id === activeId, () => onOpenChannel?.(c.id)))}
          </div>
        )}
      </div>
      <div className="sqv-comms-card">
        <h3 className="sqv-comms-title">Direct messages</h3>
        {authRequired || loading || dms.length === 0 ? (
          <StateBlock loading={loading} error={error} authRequired={authRequired} empty={dms.length === 0} emptyLabel="No direct messages yet." />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {dms.map((d) => railBtn(d.name || 'Direct message', d.id === activeId, () => onOpenDm?.(d.id), '@'))}
          </div>
        )}
      </div>
    </div>
  )
}

/**
 * CommsFrame — PHASE_2M D2 "Quantum Comms Cockpit" frame.
 *
 * One faint quantum backdrop behind every comms surface (Message Center,
 * channels/DMs, Conference Calls, Team, Tasks) + a real mobile layout:
 * under 900px the three panes become three SCREENS (Conversations / Thread /
 * Context) with an explicit switcher — not a shrunken desktop. Pure CSS
 * media queries + one state bit; no layout shift on desktop.
 */
export default function CommsFrame({ activeTop, breadcrumb, title, statusBadge, realtimeBadge, leftRail, center, rightRail, onHome, onBackCommandCenter, onNavTop, surface }: CommsFrameProps) {
  const cols = leftRail
    ? (rightRail ? '260px minmax(0, 1fr) 300px' : '260px minmax(0, 1fr)')
    : (rightRail ? 'minmax(0, 1fr) 300px' : 'minmax(0, 1fr)')
  const [mobilePane, setMobilePane] = useState<'list' | 'main' | 'context'>('main')
  const paneBtn = (key: 'list' | 'main' | 'context', label: string, present: boolean) => present ? (
    <button
      key={key}
      type="button"
      onClick={() => setMobilePane(key)}
      className="sqv-comms-panebtn"
      data-active={mobilePane === key ? 'true' : 'false'}
    >
      {label}
    </button>
  ) : null
  return (
    <CommandCenterShell
      onHome={onHome}
      onBackCommandCenter={onBackCommandCenter}
      breadcrumb={breadcrumb}
      title={title}
      titleAccent={COMMS_ACCENT}
      statusBadge={statusBadge}
      honestyBadge={realtimeBadge ? {
        label: 'REALTIME_DEFERRED · NOT FAKED',
        title: 'Communications are request/response — messages load on open, on post, and on refresh. No websockets, presence, or typing indicators yet (REALTIME_DEFERRED). Nothing here fabricates channels, members, messages, or tasks; unapplied migrations show honest backend-not-ready states.',
      } : undefined}
      topNav={{
        items: [{ id: 'communications', label: 'Communications', accent: COMMS_ACCENT }, { id: 'team', label: 'Team', accent: COMMS_ACCENT }],
        activeId: activeTop,
        onSelect: (id) => onNavTop?.(id === 'team' ? TEAM_ROOT : COMMS_ROOT),
      }}
    >
      <style>{`
        .sqv2-comms-pane-switch { display: none; }
        @media (max-width: 900px) {
          .sqv2-comms-grid { display: flex !important; flex-direction: column; }
          .sqv2-comms-pane-switch { display: flex !important; gap: 8px; margin-bottom: 12px; position: sticky; top: 0; z-index: 2; }
          .sqv2-comms-grid[data-mobile-pane="list"] .sqv2-comms-pane:not([data-pane="list"]) { display: none; }
          .sqv2-comms-grid[data-mobile-pane="main"] .sqv2-comms-pane:not([data-pane="main"]) { display: none; }
          .sqv2-comms-grid[data-mobile-pane="context"] .sqv2-comms-pane:not([data-pane="context"]) { display: none; }
        }
        /* Message action row (REPLY/COPY/TASK/BLOCKER/DELETE): revealed on
           hover AND keyboard focus, so the real audited actions are reachable
           without a mouse. Actions stay in the a11y tree at all times (no
           aria-hidden) — the fade is purely visual declutter. Always shown on
           touch (no hover) and motion-safe under reduced-motion. */
        .sqv2-msg-actions { opacity: 0; transition: opacity .15s ease; }
        .sqv2-msg:hover .sqv2-msg-actions,
        .sqv2-msg:focus-within .sqv2-msg-actions { opacity: 1; }
        @media (hover: none) { .sqv2-msg-actions { opacity: 1; } }
        @media (prefers-reduced-motion: reduce) { .sqv2-msg-actions { transition: none; } }
      `}</style>
      <div className="sqv-surface sqv-comms" data-sqv-surface={surface} style={{ position: 'relative' }}>
        <CommsQuantumBackdrop />
        <div style={{ position: 'relative', zIndex: 1 }}>
          <div className="sqv2-comms-pane-switch" aria-label="Comms panes">
            {paneBtn('list', 'Conversations', !!leftRail)}
            {paneBtn('main', 'Thread', true)}
            {paneBtn('context', 'Context', !!rightRail)}
          </div>
          <div className="sqv2-comms-grid" data-mobile-pane={mobilePane} style={{ display: 'grid', gridTemplateColumns: cols, gap: 16, alignItems: 'start' }}>
            {leftRail && <div className="sqv2-comms-pane" data-pane="list">{leftRail}</div>}
            <div className="sqv2-comms-pane" data-pane="main">{center}</div>
            {rightRail && <div className="sqv2-comms-pane" data-pane="context">{rightRail}</div>}
          </div>
        </div>
      </div>
    </CommandCenterShell>
  )
}
